import React, { useEffect, useRef, useState, useMemo, memo } from "react";
import isEqual from "lodash/isEqual";
import logger from "../../../../../utils/LoggerFrontend.js";
import { RemotionPlayer } from "./GlobalVideoGeneratorPreview.styled.js";
import { getPrintFontSize } from "../../utils.js";
import { VIDEO_FPS } from "../VideoGenerator__Preview/VideoGenerator__Preview.constants.js";

/**
 * GlobalVideoGeneratorPreview component
 *
 * @param {{
 * remotionTemplate: JSX.Element,
 * height: number,
 * width: number,
 * videoData: Object,
 * scaleFactor: number,
 * showLandscape: boolean,
 * orientation: string,
 * textLength: number,
 * isPrint: boolean
 * }} params - Props
 */
const GlobalVideoGeneratorPreview = ({
  remotionTemplate,
  height,
  width,
  videoData,
  fullWidth = false,
  duration = null,
  scaleFactor,
  showLandscape,
  orientation,
  textLength,
  isPrint,
  isAssistant = false
}) => {
  const playerRef = useRef(null);
  const videoDataRef = useRef(null);
  const playerIdRef = useRef("player-id");
  const [videoPlayerData, setVideoPlayerData] = useState(null);

  const filteredVideoData = useMemo(() => {
    if (!videoData) return null;
    return videoData;
  }, [videoData]);

  useEffect(() => {
    const textEditingAssistantInput = document.getElementById("text-editing-assistant");

    const handleFocus = () => {
      if (playerRef.current) {
        playerRef.current.pause();

        setTimeout(() => {
          if (textEditingAssistantInput) {
            textEditingAssistantInput.focus();
          }
        }, 0);
      }
    };

    if (textEditingAssistantInput) {
      textEditingAssistantInput.addEventListener("focus", handleFocus);
    }

    return () => {
      if (textEditingAssistantInput) {
        textEditingAssistantInput.removeEventListener("focus", handleFocus);
      }
    };
  }, []);

  useEffect(() => {
    logger.log("GlobalVideoGeneratorPreview:useEffect", "called", { videoData });

    const hasChanged = !isEqual(filteredVideoData, videoDataRef.current);
    if (hasChanged) {
      videoDataRef.current = filteredVideoData;
      setVideoPlayerData(videoData);
    }
  }, [filteredVideoData]);

  const resetVideo = () => {
    if (playerRef.current) playerRef.current.seekTo(0);
  };

  useEffect(() => {
    logger.log("GlobalVideoGeneratorPreview:useEffect", "called", { videoPlayerData });

    if (!videoPlayerData) return;
    resetVideo();
  }, [videoPlayerData]);

  const uploadedVideoDuration = useMemo(
    () => Math.floor(videoPlayerData?.duration || 1) * VIDEO_FPS,
    [videoPlayerData]
  );

  const printFontSize = isPrint ? getPrintFontSize(orientation, textLength) * scaleFactor : null;

  const playerControls = isAssistant
    ? { autoPlay: false, controls: true, loop: false, initiallyMuted: true }
    : { autoPlay: false, controls: true, loop: true, initiallyMuted: false };

  return (
    <RemotionPlayer
      ref={playerRef}
      id={playerIdRef.current}
      key={`${filteredVideoData?.video}-${filteredVideoData?.backgroundImage}`}
      component={remotionTemplate}
      durationInFrames={duration || uploadedVideoDuration}
      fps={VIDEO_FPS}
      compositionWidth={width || 1080}
      compositionHeight={height || 1920}
      style={{ width: fullWidth ? "100%" : width }}
      inputProps={videoPlayerData}
      scaleFactor={scaleFactor}
      showLandscape={showLandscape}
      printFontSize={printFontSize}
      {...playerControls}
    />
  );
};

export default memo(GlobalVideoGeneratorPreview);
